import { IdMap, RArray, RSet } from "../collections";
import { cartesianProduct, toPairs } from "../core";
import { makeImmutable } from "../core/makeImmutable";
import { Holiday, MenuSectionId, MenuSectionItemId, ModifierItemTypeId, ModifierTypeId, Money, OrderingHours, OrderingPolicy, ProductInstanceParser, PromotionParser, Restaurant, RestaurantId, WeeklySchedule, } from "../ordering";
import { automaticCrossSellingCategoryFilter } from "../ordering/CrossSellingListProvider/AutomaticCrossSellingCategoryFilter";
import { CrossSellingListParser, MenuV2Parser } from "../parsing";
import { LocalizedString } from "../presentation/LocalizedString";
import { PCustomParameterChoice } from "../presentation/PCustomParameterChoice";
import { PCustomParameterType } from "../presentation/PCustomParameterType";
import { PMenu } from "../presentation/PMenu";
import { PMenuSection } from "../presentation/PMenuSection";
import { PMenuSectionItem } from "../presentation/PMenuSectionItem";
import { PModifierItemSection } from "../presentation/PModifierItemSection";
import { PModifierItemType } from "../presentation/PModifierItemType";
import { PModifierType, } from "../presentation/PModifierType";
import { PPackagingContainerType } from "../presentation/PPackagingContainerType";
import { PProductInstanceVariant } from "../presentation/PProductInstanceVariant";
import { PProductType } from "../presentation/PProductType";
import { PRestaurant } from "../presentation/PRestaurant";
import { PromotionService, } from "../services";
import { AutomaticCrossSellingWeightsParser } from "./AutomaticCrossSellingWeightsParser";
import { PPromotionParser } from "./PPromotionParser";
import { PackagingContainerParser } from "./PackagingContainerParser";
export class PRestaurantParser {
    constructor(params) {
        this.restaurantId = new RestaurantId(params.input.id);
        this.localizationProvider = params.localizationProvider;
        this.input = params.input;
        this.rpcService = params.rpcService;
        this.checkoutInfoService = params.checkoutInfoService;
        this.holidayService = params.holidayService;
        this.orderingAvailabilityService = params.orderingAvailabilityService;
        this.appOrigin = params.appOrigin;
        makeImmutable(this);
    }
    get catalogue() {
        return this.input.catalogue;
    }
    get timezone() {
        return this.input.time_zone;
    }
    get promotions() {
        return this.input.promotions;
    }
    get maxOrderValue() {
        const { max_order_value } = this.input;
        return max_order_value === null ? null : Money.fromBackend(max_order_value);
    }
    get orderingPolicy() {
        return new OrderingPolicy({
            maxOrderValue: this.maxOrderValue,
            deliveryInTotal: this.input.delivery_in_total,
            enableCoupons: this.input.enable_coupons,
            enableFod: this.input.enable_fod,
            enableMarketingConsents: this.input.enable_marketing_consents,
        });
    }
    async parse() {
        const onlineOrderingSettings = this.input.online_ordering_settings;
        const domainMenuParser = new MenuV2Parser({
            catalogue: this.catalogue,
            localizationProvider: this.localizationProvider,
            appOrigin: this.appOrigin,
            onlineOrderingSettings,
        });
        const domainMenu = domainMenuParser.parseMenu();
        const promotionParser = new PPromotionParser({
            localizationProvider: this.localizationProvider,
            domain: new PromotionParser({
                appOrigin: this.appOrigin,
                customParameterTypes: domainMenu.customParameterTypes,
                productCategoryIds: domainMenu.productCategories.ids,
                productTypeIds: domainMenu.productTypes.ids,
                timezone: this.timezone,
            }),
        });
        const crossSellingListParser = new CrossSellingListParser({
            catalogue: this.catalogue,
            productTypeIds: domainMenu.productTypes.ids,
            productInstanceParser: new ProductInstanceParser({
                productCategories: domainMenu.productCategories,
                productTypes: domainMenu.productTypes,
                modifierTypes: domainMenu.modifierTypes,
                customParameterTypes: domainMenu.customParameterTypes,
            }),
        });
        const packagingContainerParser = new PackagingContainerParser({
            catalogue: this.catalogue,
        });
        const orderingAvailability = await this.orderingAvailabilityService.getOrderingAvailability(this.restaurantId);
        const orderingPossible = orderingAvailability.tag === "OrderingPossible";
        const promotionTypes = promotionParser.parsePromotions(this.promotions);
        const domainPackagingContainerTypes = packagingContainerParser.parse();
        const checkoutInfo = await this.checkoutInfoService.getCheckoutInfo(this.restaurantId, this.timezone);
        const holidays = await this.holidayService
            .getHolidays(this.restaurantId)
            .then((infos) => infos.map((info) => Holiday.fromHolidayInfo(info, this.timezone)));
        const paymentChannels = await this.checkoutInfoService.getPaymentChannels(this.restaurantId);
        const orderingHours = new OrderingHours({
            weeklySchedule: WeeklySchedule.fromWeeklyAvailability(this.input.ordering_hours),
        });
        const minOrderValue = {
            takeaway: Money.fromString(this.input.min_order_value_with_pickup),
            default: Money.fromString(this.input.min_order_value),
        };
        const automaticCrossSellingWeightsParser = new AutomaticCrossSellingWeightsParser({
            weights: await this.rpcService.rpc("GetAcsWeights", {
                restaurantId: this.restaurantId.value,
            }),
        });
        const allowedAutomaticCrossSellingProductCategories = domainMenu.productCategories.ids.filter((productCategoryId) => automaticCrossSellingCategoryFilter(this.catalogue.categories[productCategoryId.value].name));
        const domainRestaurant = Restaurant.create({
            menu: domainMenu,
            id: this.restaurantId,
            orderingPolicy: this.orderingPolicy,
            orderingHours,
            holidays,
            promotionTypes: IdMap.fromIterable(promotionTypes.objects.map((promotionType) => promotionType.domain)),
            packagingContainerTypes: domainPackagingContainerTypes,
            timezone: this.timezone,
            appOrigin: this.appOrigin,
            orderingPossible,
            checkoutInfo,
            paymentChannels,
            onlineOrderingSettings,
            minOrderValue,
            crossSellingListRepository: crossSellingListParser.parse(),
            allowedAutomaticCrossSellingProductCategories,
            automaticCrossSellingWeights: automaticCrossSellingWeightsParser.parse(),
        });
        const promotionService = new PromotionService({
            restaurantId: this.restaurantId,
            rpcService: this.rpcService,
            promotionParser,
        });
        const customParameterTypes = this.parseCustomParameterTypes(domainMenu.customParameterTypes);
        const packagingContainerTypes = this.parsePackagingContainerTypes(domainPackagingContainerTypes);
        // FIXME How to choose correct menu arragment?
        const menuArragment = domainMenu.arrangments.sample;
        if (menuArragment === null) {
            throw new Error("No menu sections defined");
        }
        const modifierTypes = this.parseModifierTypes(domainMenu.modifierTypes);
        return new PRestaurant({
            domain: domainRestaurant,
            name: this.input.name,
            address: this.input.address,
            menu: new PMenu({
                domain: domainMenu,
                sections: this.parseSections(domainMenu.productTypes, domainMenu.schedules, menuArragment.sections, customParameterTypes),
            }),
            productTypes: this.parseProductTypes(domainMenu.productTypes, domainMenu.schedules, modifierTypes),
            modifierTypes,
            customParameterTypes,
            promotionService,
            promotionTypes,
            packagingContainerTypes,
            isApplePaySupported: this.checkoutInfoService.isApplePaySupported,
            orderingAvailability,
        });
    }
    parseTranslated(value) {
        return new LocalizedString(this.localizationProvider, value);
    }
    getProductFreeModifierItemsCount(product) {
        return Object.values(product.modifierSettings)
            .map((modifierSetting) => modifierSetting.freeItems)
            .reduce((a, b) => a + b, 0);
    }
    parseProductTypes(domainProductTypes, domainSchedules, modifierTypes) {
        return IdMap.fromIterable(toPairs(this.catalogue.products).mapOptional(([productId, product]) => {
            const domainProductType = domainProductTypes.rawFind(productId);
            if (domainProductType === null) {
                return null;
            }
            return new PProductType({
                domain: domainProductType,
                name: this.parseTranslated(product.name),
                tags: product.tags,
                photo: product.photo,
                description: this.parseTranslated(product.description),
                allergens: RSet.fromIterable(product.allergens.map(([allergen]) => allergen), { ignoreDuplicates: true }),
                freeModifierItemsCount: this.getProductFreeModifierItemsCount(product),
                baseIngredients: this.getProductBaseIngredientsString(product, this.catalogue.categories[product.category], modifierTypes),
                availabilitySchedule: product.availabilitySchedule === null
                    ? null
                    : domainSchedules.rawGet(product.availabilitySchedule),
                availability: product.availability,
                divisibleModifiersOrder: product.divisibleModifiersOrder.map((modifierId) => new ModifierTypeId(modifierId)),
                nonDivisibleModifiersOrder: product.nonDivisibleModifiersOrder.map((modifierId) => new ModifierTypeId(modifierId)),
            });
        }));
    }
    getProductBaseIngredientsString(product, productCategory, modifierTypes) {
        const modifierIds = [
            ...product.productModifiers,
            ...productCategory.categoryModifiers,
        ];
        return modifierIds
            .map((modifierId) => modifierTypes.rawGet(modifierId))
            .map((modifierType) => this.getProductModifierDefaultItemsNames(product, modifierType))
            .filter((v) => v)
            .join(", ");
    }
    getProductModifierDefaultItemsNames(product, modifier) {
        const modifierSettings = product.modifierSettings[modifier.id.value];
        if (modifierSettings === undefined ||
            modifierSettings.displayDefaultItems === false ||
            modifierSettings.defaultItems === null) {
            return null;
        }
        const defaultItems = new RArray(modifierSettings.defaultItems.map(([itemId, quantity]) => [new ModifierItemTypeId(itemId), quantity]));
        return modifier.getItemsDescription(defaultItems);
    }
    parseModifierTypes(domainModifiers) {
        return IdMap.fromIterable(toPairs(this.catalogue.modifiers).mapOptional(([modifierId, modifier]) => {
            const domainModifier = domainModifiers.rawFind(modifierId);
            if (domainModifier === null) {
                return null;
            }
            return new PModifierType({
                domain: domainModifier,
                name: this.parseTranslated(modifier.name),
                description: modifier.description
                    ? this.parseTranslated(modifier.description)
                    : null,
                displayMode: this.parseDisplayMode(modifier.displayMode),
                sections: this.parseModifierSections(domainModifier.itemTypes, modifier),
            });
        }));
    }
    parseCustomParameterTypes(domainCustomParameterTypes) {
        return IdMap.fromIterable(toPairs(this.catalogue.customParameters).mapOptional(([customParameterId, customParameter]) => {
            const domainCustomParameterType = domainCustomParameterTypes.rawFind(customParameterId);
            if (domainCustomParameterType === null) {
                return null;
            }
            return new PCustomParameterType({
                domain: domainCustomParameterType,
                name: this.parseTranslated(customParameter.name),
                choices: this.parseCustomParameterChoices(domainCustomParameterType.choices, customParameter),
            });
        }));
    }
    parseDisplayMode(displayMode) {
        switch (displayMode) {
            case "ModifierList":
                return "StepperList";
            case "ModifierCheckboxList":
                return "CheckBoxList";
            case "ModifierRadioButtonList":
                return "RadioList";
            case "ModifierPizzaGrid":
                return "PizzaGrid";
            // FIXME: handle Hidden modifiers
            case "ModifierHidden":
                return "StepperList";
        }
    }
    parseModifierSections(domainModifierItemTypes, modifier) {
        return modifier.sections.map((section) => {
            return new PModifierItemSection({
                name: section.name ? this.parseTranslated(section.name) : null,
                items: new RArray(section.items).mapOptional(([modifierItemTypeId, modifierItemType]) => {
                    const domainModifierItemType = domainModifierItemTypes.rawFind(modifierItemTypeId);
                    if (domainModifierItemType === null) {
                        return null;
                    }
                    return new PModifierItemType({
                        domain: domainModifierItemType,
                        localizationProvider: this.localizationProvider,
                        name: this.parseTranslated(modifierItemType.name),
                        photo: modifierItemType.photo,
                    });
                }).raw,
            });
        });
    }
    parseCustomParameterChoices(domainCustomParameterChoices, customParameter) {
        return new RArray(customParameter.choices).mapOptional(([choiceId, choice]) => {
            const domainCustomParameterChoice = domainCustomParameterChoices.rawFind(choiceId);
            if (domainCustomParameterChoice === null) {
                return null;
            }
            return new PCustomParameterChoice({
                domain: domainCustomParameterChoice,
                name: this.parseTranslated(choice.name),
            });
        }).raw;
    }
    parseSections(domainProducts, domainSchedules, domainMenuSections, customParameters) {
        return new RArray(toPairs(this.catalogue.menus).raw.flatMap(([_, menu]) => menu.sections)).mapOptional((sectionId) => {
            const section = this.catalogue.sections[sectionId];
            const scheduleId = section.visibilitySchedule;
            const domainSection = domainMenuSections.find(new MenuSectionId(sectionId));
            if (!domainSection) {
                return null;
            }
            return new PMenuSection({
                domain: domainSection,
                name: this.parseTranslated(section.name),
                // TODO: do we want to process it here (resize etc.)?
                photoUrl: section.photo,
                items: this.parseSectionItems(sectionId, section, domainProducts, customParameters, domainMenuSections, domainSchedules),
                visibilitySchedule: scheduleId === null ? null : domainSchedules.rawGet(scheduleId),
            });
        }).raw;
    }
    parseSectionItems(id, section, domainProductTypes, customParameters, domainMenuSections, domainSchedules) {
        if (section.content.tag !== "Products") {
            return [];
        }
        return new RArray(section.content.items).mapOptional(([itemId, item]) => {
            if (item.tag !== "SingleProduct") {
                return null;
            }
            const productId = item.itemProduct;
            const domainProductType = domainProductTypes.rawFind(productId);
            if (domainProductType === null) {
                return null;
            }
            const product = this.catalogue.products[productId];
            const dropdownParameterIds = section.dropdownParameters;
            const dropdownCustomParameters = new RArray(dropdownParameterIds).map((dropdownParameterId) => customParameters.rawGet(dropdownParameterId));
            const dropdownParametersPair = dropdownCustomParameters.map((parameter) => [
                parameter,
                new RArray(parameter.choices.map((choice) => choice.name)),
            ]);
            const parameterCombinations = cartesianProduct(dropdownParametersPair);
            const availabilitySchedule = product.availabilitySchedule === null
                ? null
                : domainSchedules.rawGet(product.availabilitySchedule);
            // NOTICE Defensive code due to legacy MenuSections in peeprs pizza menu
            const domainMenuSection = domainMenuSections.rawFind(id);
            if (domainMenuSection === null) {
                return null;
            }
            const productVariants = domainMenuSection.variants.rawFind(productId);
            if (productVariants === null) {
                return null;
            }
            return new PMenuSectionItem({
                id: new MenuSectionItemId(itemId),
                name: this.parseTranslated(product.name),
                variants: productVariants.map((variant, index) => new PProductInstanceVariant({
                    domain: variant,
                    menuSectionId: domainMenuSection.id,
                    name: Array.from(parameterCombinations.get(index).values()).join(" / "),
                    localizationProvider: this.localizationProvider,
                })).raw,
                availabilitySchedule,
            });
        }).raw;
    }
    parsePackagingContainerTypes(domainPackagingContainerTypes) {
        return IdMap.fromIterable(toPairs(this.catalogue.packagingContainers).mapOptional(([packagingContainerId, packagingContainer]) => {
            const domain = domainPackagingContainerTypes.rawFind(packagingContainerId);
            if (domain === null) {
                return null;
            }
            return new PPackagingContainerType({
                domain,
                name: this.parseTranslated(packagingContainer.name),
            });
        }));
    }
}
